import { formatYYYYMMLong } from './overviewMonthUtils';
import { tierConfig } from './theme';

/** Label from session `room` (operations) or `roomName` (overview history). */
function sessionRoomLabel(s) {
  const fromRoom = s?.room != null ? String(s.room).trim() : '';
  if (fromRoom && fromRoom !== '—') return fromRoom;
  if (s?.roomName != null && String(s.roomName).trim()) return String(s.roomName).trim();
  return '';
}

function shortDateFromSessionLog(sessionLog) {
  const s = Array.isArray(sessionLog) ? sessionLog[0] : null;
  if (!s?.date) return '—';
  const raw = String(s.date).trim();
  const slash = raw.split('/');
  if (slash.length === 3) {
    const d = new Date(parseInt(slash[2], 10), parseInt(slash[1], 10) - 1, parseInt(slash[0], 10));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
  }
  return raw;
}

/**
 * When GET /client/overview does not support calcMonth (no appliedCalcMonth), rebuild
 * month-scoped aggregates from GET /client/operations for the same window.
 */
export function buildOverviewFallbackFromOperations(opsPayload, overviewPayload, selectedMonth) {
  if (!opsPayload?.success || !overviewPayload) return null;
  const sessionLog = Array.isArray(opsPayload.sessionLog) ? opsPayload.sessionLog : [];
  const summary = opsPayload.summary || {};
  const hoursThisMonth =
    summary.hours != null
      ? Number(summary.hours)
      : sessionLog.reduce((acc, row) => {
          const m = String(row.duration || '').match(/(\d+)/);
          return acc + (m ? parseInt(m[1], 10) / 60 : 0);
        }, 0);
  const roundedHours = Math.round(hoursThisMonth * 10) / 10;
  const sessions = summary.sessions != null ? summary.sessions : sessionLog.length;

  const byMapRoomName = new Set(
    sessionLog
      .map((s) => sessionRoomLabel(s))
      .filter((n) => n && n !== 'Other' && n !== '—')
  );
  const roomsCovered = byMapRoomName.size;

  const tierRaw = overviewPayload.currentPlan?.tier;
  const tierKey =
    tierRaw && typeof tierRaw === 'string'
      ? Object.keys(tierConfig).find((k) => k.toLowerCase() === tierRaw.trim().toLowerCase()) || null
      : null;
  const planHours = tierKey ? tierConfig[tierKey]?.hours || 36 : 36;
  const tierUsagePct =
    tierKey && planHours > 0 ? Math.round((roundedHours / planHours) * 100) : overviewPayload.kpis?.tierUsagePct ?? null;

  const areaCounts = {};
  sessionLog.forEach((s) => {
    const n = sessionRoomLabel(s);
    const area = n || 'Other';
    areaCounts[area] = (areaCounts[area] || 0) + 1;
  });
  const total = Object.values(areaCounts).reduce((a, b) => a + b, 0) || 1;
  const coverage = Object.entries(areaCounts).map(([name, count]) => ({
    name,
    count,
    value: Math.round((count / total) * 100),
  }));

  const recentActivity = sessionLog.slice(0, 5).map((s) => ({
    roomName: sessionRoomLabel(s) || null,
    room: sessionRoomLabel(s) || '—',
    date: s.date || '—',
    time: s.start || '—',
    duration: s.duration || '—',
  }));

  const monthTitle = formatYYYYMMLong(selectedMonth);

  return {
    appliedCalcMonth: selectedMonth,
    kpis: {
      ...overviewPayload.kpis,
      hoursThisMonth: roundedHours,
      sessions,
      roomsCovered,
      tierUsagePct,
      lastService: shortDateFromSessionLog(sessionLog),
    },
    coverage,
    recentActivity,
    overdueRooms: [],
    briefing: {
      ...overviewPayload.briefing,
      month: monthTitle,
      narrative: `In ${monthTitle}, operations data shows ${sessions} session(s) totaling ${roundedHours} UVC hours in the selected period (${roomsCovered} room(s) with logged activity). Connect an updated overview API for full KPI parity and overdue tracking by month.`,
    },
  };
}
